/**
 * Guardian surfaces (integration guide §8).
 *
 * Authorisation here is the GRANTED GUARDIAN LINK, never the `guardian` role.
 * Being a guardian somewhere grants nothing; being *this* child's linked guardian
 * grants a tier-filtered view of *this* child. A revoked or pending link is not a
 * link — which is why {@link OrgGuardianFacade} never consults `/org/context`
 * (trap 20: a guardian-only user gets `member: null` there and still has children).
 */

/** The privacy gradient, derived server-side from the child's stage (§8.1). */
export type OrgGuardianTier = 'full' | 'summary' | 'minimal';

/**
 * Every section name the digest can carry. `conversation_summary` is a generated
 * summary of topics and tone — NEVER a transcript.
 */
export type OrgDigestSectionKey =
  | 'status'
  | 'escalation_alerts'
  | 'mood_trend'
  | 'program_progress'
  | 'appointments'
  | 'conversation_summary';

export interface OrgChild {
  /** An ORG MEMBER id, not a user id (trap 14) — this is the `/children/{id}/digest` path id. */
  memberId: number;
  name: string;
  cohort: string | null;
  stage: string | null;
  relationship: string | null;
  tier: string;
  /** Ready-to-render explanation of what this tier lets the guardian see. */
  tierNote: string;
  /** The sections this tier permits, in server order. */
  sections: readonly string[];
}

/**
 * `GET /children/{member_id}/digest`.
 *
 * {@link sections} is a SPARSE MAP keyed by section name — a section with nothing
 * to show is omitted (trap 15). Iterate {@link availableSections} and look each
 * one up; never assume every allowed section is present. Sections are assembled
 * from the allowed list rather than built-then-filtered, so an unauthorised
 * section is never constructed at all.
 *
 * Every digest read is AUDITED server-side — the same promise made to the student.
 */
export interface OrgChildDigest {
  child: Omit<OrgChild, 'tier' | 'tierNote' | 'sections'>;
  tier: string;
  tierNote: string;
  /** Raw section payloads, shape-per-section. Rendered generically — see the digest view. */
  sections: Readonly<Record<string, unknown>>;
  availableSections: readonly string[];
}

/** The `available_sections` order, each paired with whatever the sparse map holds. */
export interface OrgDigestSection {
  key: string;
  /** Undefined when the section is allowed but had nothing to show. */
  value: unknown;
  present: boolean;
}

/** Walk `available_sections` and look each one up — the ONLY correct iteration (trap 15). */
export function digestSections(digest: OrgChildDigest): readonly OrgDigestSection[] {
  return digest.availableSections.map((key) => {
    const present = Object.prototype.hasOwnProperty.call(digest.sections, key);
    return { key, value: present ? digest.sections[key] : undefined, present };
  });
}

/** A consent request waiting on this guardian's decision (§8.3). */
export interface OrgConsentRequest {
  referralId: number;
  childName: string;
  caseType: string | null;
  priority: string | null;
  /** ISO 8601. */
  askedAt: string | null;
  /**
   * What the guardian is being asked to see. SHOW THIS on the decision screen —
   * consent with no stated scope is not informed consent (§8.3).
   */
  scope: readonly string[];
  note: string;
}

/** `POST /consents/{referral_id}` success (§8.4). */
export interface OrgConsentDecision {
  referralId: number;
  /** e.g. `consent_granted` — the state the referral moved to. */
  guardianState: string;
}

/** Max length the server accepts on a consent note (§8.4). */
export const ORG_CONSENT_NOTE_MAX = 500;

/**
 * Is this guardian able to act at all? An empty children list AND an empty
 * consent list is the honest "you are not a guardian here" state — a 200 with
 * `{list: []}`, never a 403 (§8.1, §8.3).
 */
export function hasGuardianSurface(
  children: readonly OrgChild[],
  consents: readonly OrgConsentRequest[],
  requests: readonly unknown[],
): boolean {
  return children.length > 0 || consents.length > 0 || requests.length > 0;
}
